import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from '@config/server/apiClient';
import { productOnboardingEndpoints } from '@config/server/apiEndpoints';

import { productOnboarding } from './queryKeys';

export interface ProductPricingData {
	id: string;
	priceId: string;
	/** Display price in cents (e.g. 29900 = $299). */
	displayPrice?: number;
}

/** Map of productId → pricing data returned by the backend. */
export type ProductPricingMap = Record<string, ProductPricingData>;

/**
 * Fetch live product pricing from the backend.
 *
 * - Without `svpId`: calls the public `/pricing` endpoint (default prices).
 * - With `svpId`: calls the protected `/billing/pricing` endpoint (SVP-specific overrides).
 */
export const useGetProductPricing = (svpId?: string): UseQueryResult<ProductPricingMap> =>
	useQuery({
		...productOnboarding.pricing(svpId),
		queryFn: async (): Promise<ProductPricingMap> => {
			const url = svpId
				? productOnboardingEndpoints.svpPricing(svpId)
				: productOnboardingEndpoints.pricing();
			const res = await apiClient.get<ProductPricingMap>(url);
			return res.data as ProductPricingMap;
		},
		staleTime: 5 * 60 * 1000,
	});
